import React, { useEffect, useRef } from 'react';
import { useSession } from '../context/SessionContext';
import { useAppStore } from '../store/useAppStore';
import {
  redirectToPlatformAuth,
  redirectToPlatformVerify,
  takeVerifyToken,
  redirectToPlatformReset,
  takeResetToken,
  verifyReturnState,
} from './platformAuthRedirect';

/**
 * The single auth gate for the app — mirrors sk-catalog-frontend's
 * rootNavigator.tsx boot logic. Drive owns no login/signup/verify/reset
 * screen: on boot it probes the platform session (via SessionProvider) and
 * either renders the app or bounces to the platform's auth app. No
 * iframe/postMessage bridge — SK Catalog's product apps are reached by
 * full-page redirect only.
 */
export default function RootGate({ children }: { children: React.ReactNode }) {
  const { user, org, role, loading, error } = useSession();
  const setSession = useAppStore(s => s.setSession);
  const clearSession = useAppStore(s => s.clearSession);
  const handedOff = useRef(false);

  useEffect(() => {
    // The drive verification/reset email links back here (?mode=verify|reset
    // &token=…). SK Drive does NOT own authentication — the SK Platform owns
    // the ONLY verify / reset-password screen for every product, so we NEVER
    // handle these locally. Hand the token to the platform unconditionally,
    // before the session probe even resolves, and stop.
    const verifyToken = takeVerifyToken();
    if (verifyToken) { handedOff.current = true; redirectToPlatformVerify(verifyToken); return; }

    const resetToken = takeResetToken();
    if (resetToken) { handedOff.current = true; redirectToPlatformReset(resetToken); return; }
  }, []);

  useEffect(() => {
    if (handedOff.current || loading) return;

    if (error === 'unauthenticated') {
      clearSession();
      redirectToPlatformAuth();
      return;
    }

    if (error === 'unreachable') {
      // Platform is down/unreachable, not "logged out" — don't redirect, or
      // an outage would bounce every user into a redirect loop.
      clearSession();
      return;
    }

    if (!verifyReturnState()) {
      clearSession();
      redirectToPlatformAuth();
      return;
    }

    // Verified but profile (Step 2) still pending → finish it on the
    // platform, which owns the "Tell us about you" card and returns here.
    if (user?.profile_complete === false) {
      clearSession();
      redirectToPlatformAuth();
      return;
    }

    setSession(user, org, role);
  }, [loading, error, user, org, role, setSession, clearSession]);

  if (handedOff.current) return <BootScreen message="Redirecting…" />;
  if (loading) return <BootScreen />;
  if (error === 'unreachable') return <BootScreen message="Can't reach SK Platform. Retrying…" />;
  if (!user || !org) return <BootScreen message="Redirecting to sign in…" />;

  return <>{children}</>;
}

function BootScreen({ message = 'Loading SK Drive…' }: { message?: string }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--paper)', color: 'var(--ink-3)', fontSize: 14, fontFamily: 'var(--font-sans)',
    }}>
      {message}
    </div>
  );
}
